import React, { FC, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { setPath, setUrlParam } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CalendarView from './CalendarView'
import MemberBlock from './MemberBlock'
import { format, nowDate, startOfLastWeek, weekList } from '../../../../utils/date'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import { uniqueId } from 'lodash'
import { updateReadReport } from '../../../../states/containers/report/reportSlice'

type LangValue = 'ja' | 'en'

interface Props {
  team: {
    id: number
    name: string
    reports: {
      date: string
      total_evaluation: string
      weekly_reports: {
        date: string
        is_read: boolean
      }[]
      is_read: boolean
    }[]
    managers: {
      id: number
      name: string
      image_url: string | null
    }[]
    members: {
      id: number
      name: string
      image_url: string | null
    }[]
  }
  year: string
  openMemberModalCallback: (param: any) => void
}

const TeamList: FC<Props> = (props) => {
  const {
    team,
    year,
    openMemberModalCallback,
  } = props

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [isDisplayMember, setIsDisplayMember] = useState<boolean>(false)
  const [displayMonth, setDisplayMonth] = useState<number>(0)

  const toggleMemberBlock = () => {
    setIsDisplayMember(!isDisplayMember)
  }

  const displayRange = useMemo(() => {
    if (!displayMonth) return []
    return weekList(`${year}-${displayMonth}-01`, "YYYY-MM-DD")
  }, [displayMonth, year])

  const weeklyReports = useMemo(() => {
    if (!displayMonth) return []
    return team.reports.find((report) => Number(format(report.date, "M")) === displayMonth)?.weekly_reports
  }, [displayMonth])

  const nextMonth = () => {
    if (
      Number(format(`${year}-${displayMonth}-01`, "YYYYMM")) >= Number(format(nowDate(), "YYYYMM"))
      || displayMonth === 12
    ) return
    setDisplayMonth(displayMonth + 1)
  }

  const prevMonth = () => {
    if (
      Number(format(`${year}-${displayMonth}-01`, "YYYYMM")) < Number(format(commonPart.company?.created_at || "2019-01-01", "YYYYMM"))
      || displayMonth === 1
    ) return
    setDisplayMonth(displayMonth - 1)
  }

  const clickReport = (id: number, range: string) => {
    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL_WEEKLY.path,
      [
        { key: 'target', value: "team" },
        { key: 'id', value: id.toString() },
      ],
    ), language)}?date=${range}`)
    dispatch(updateReadReport({ id: id, target: "team", type: "weekly", date: range }))
  }

  useEffect(() => {
    if (!team) return
    if (Number(year) < Number(nowDate("Y"))) {
      setDisplayMonth(12)
    } else {
      setDisplayMonth(Number(format(startOfLastWeek(nowDate()), "M")))
    }
  }, [team])

  return (
    <div className="content-block">
      <div className="block-header">
        <div className="title no_trans">
          {team.name}
        </div>
        <div className="weekly_list">
          {
            Number(format(`${year}-${displayMonth}-01`, "YYYYMM")) < Number(format(commonPart.company?.created_at || "2019-01-01", "YYYYMM"))
            || displayMonth === 1 || (
              <div
                className="change_month"
                onClick={() => prevMonth()}
                role="none"
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </div>
            )
          }
          <div className="graph_area">
            <div className="line" />
            {
              displayRange?.map((range) => (
                <div className="block" key={`${team.id}_${range}`}>
                  {
                    weeklyReports?.find((report) => report.date === range)?.date ? (
                      <>
                        <div className="check">
                          <FontAwesomeIcon icon={['fas', 'check']} />
                        </div>
                        <div className="text_area">
                          {range}
                          {
                            weeklyReports?.find((report) => report.date === range)?.is_read || (
                              <div className="new">
                                New
                              </div>
                            )
                          }
                        </div>
                        <button
                          data-gtm-click="weekly_report - header - button"
                          type="button"
                          className="view_report"
                          onClick={() => clickReport(team.id, range)}
                        >
                          {t('report.list.box.view_report')}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="no_check" />
                        <div className="text_area">
                          {range}
                        </div>
                      </>
                    )
                  }
                </div>
              ))
            }
          </div>
          {
            Number(format(`${year}-${displayMonth}-01`, "YYYYMM")) >= Number(format(nowDate(), "YYYYMM"))
            || displayMonth === 12 || (
              <div
                className="change_month"
                onClick={() => nextMonth()}
                role="none"
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </div>
            )
          }
        </div>
      </div>
      <div className="block-detail">
        <div className="card_area">
          {
            Array(12).fill(null).map((val, key) => (
              <React.Fragment key={uniqueId()}>
                <CalendarView
                  target="team"
                  id={team.id}
                  year={year}
                  month={(key + 1).toString()}
                  list={team.reports.find((value) => format(value.date, "M") === (key + 1).toString())}
                />
              </React.Fragment>
            ))
          }
        </div>
      </div>
      <div
        data-gtm-click="open_member - button"
        className="block-header members"
        onClick={() => toggleMemberBlock()}
        role="none"
      >
        <div className="title">
          {t('report.list.box.member')}
        </div>
        <div className="icon">
          {
            isDisplayMember ? (
              <FontAwesomeIcon icon={['fas', 'angle-up']} />
            ) : (
              <FontAwesomeIcon icon={['fas', 'angle-down']} />
            )
          }
        </div>
      </div>
      <div className={`block-detail member ${isDisplayMember || "none"}`}>
        <div>
          <div className="manager_card_area">
            {
              team.managers.map((manager) => (
                <React.Fragment key={uniqueId()}>
                  <MemberBlock
                    type="manager"
                    name={manager.name}
                    id={manager.id}
                    icon={manager.image_url}
                    openMemberModalCallback={(param) => openMemberModalCallback(param)}
                  />
                </React.Fragment>
              ))
            }
          </div>
          <div className="member_card_area">
            {
              team.members.map((member) => (
                <React.Fragment key={uniqueId()}>
                  <MemberBlock
                    type="member"
                    name={member.name}
                    id={member.id}
                    icon={member.image_url}
                    openMemberModalCallback={(param) => openMemberModalCallback(param)}
                  />
                </React.Fragment>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamList
